import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Clock, Globe2, MapPin, Users, Banknote, Timer, GraduationCap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Reveal, Field, inputCls, PriceTag } from "@/components/ui-bits";
import { imageUrl } from "@/lib/images";

const DELIVERY = ["online", "in-person", "hybrid", "on-site"];

export default function CourseDetail() {
  const { id } = useParams();
  const { t, pick } = useLanguage();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sending, setSending] = useState(false);
  const [payment, setPayment] = useState(null);
  const [form, setForm] = useState({});
  const [refForm, setRefForm] = useState({ reference: "", proof: null });
  const [paySending, setPaySending] = useState(false);
  const [payDone, setPayDone] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => {
      setCourse(r.data);
      document.title = `${r.data.title_en} | Stratedge UK`;
    }).catch(() => setNotFound(true));
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/course-registrations", {
        course_id: id,
        full_name: form.full_name || "", email: form.email || "",
        phone: form.phone || "", whatsapp: form.whatsapp || "",
        country: form.country || "", city: form.city || "",
        organization: form.organization || "", job_title: form.job_title || "",
        preferred_date: form.preferred_date || "", delivery_method: form.delivery_method || "",
        participants: parseInt(form.participants || "1", 10) || 1,
        notes: form.notes || "",
      });
      toast.success(t("reg.success"));
      setPayment({
        regPk: data.id, registration_id: data.registration_id,
        amount_due: data.amount_due, currency: data.currency,
        en: data.wise_instructions_en, ar: data.wise_instructions_ar,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    setPaySending(true);
    try {
      const fd = new FormData();
      fd.append("reference", refForm.reference);
      if (refForm.proof) fd.append("proof", refForm.proof);
      await api.post(`/registrations/${payment.regPk}/payment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPayDone(true);
      toast.success(t("reg.payDone"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPaySending(false);
    }
  };

  if (notFound)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-500" data-testid="course-not-found">{t("course.notFound")}</div>;
  if (!course)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-400">{t("common.loading")}</div>;

  const meta = [
    [Clock, t("course.duration"), course.duration],
    [Globe2, t("course.delivery"), t(`delivery.${course.delivery_method}`) !== `delivery.${course.delivery_method}` ? t(`delivery.${course.delivery_method}`) : course.delivery_method],
    [GraduationCap, t("course.language"), course.language],
    [MapPin, t("course.location"), course.location],
    [CalendarDays, t("course.dates"), [course.start_date, course.end_date].filter(Boolean).join(" — ")],
    [Timer, t("course.schedule"), course.schedule],
    [Users, t("course.seats"), course.seats],
    [Banknote, t("course.fee"), course.fee ? `${course.currency} ${Number(course.fee).toLocaleString()}` : ""],
  ].filter((m) => m[2]);

  const listBlock = (title, items) =>
    items?.length > 0 && (
      <div>
        <h3 className="mb-3 font-display text-xl font-semibold text-navy">{title}</h3>
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {item}
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <div data-testid="course-detail-page">
      <section className="relative h-72 overflow-hidden bg-navy-950 sm:h-96">
        <img src={imageUrl(course.featured_image)} alt={pick(course, "title")} className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-12">
          <Link to="/training" data-testid="course-back-link" className="mb-4 inline-block text-xs font-bold uppercase tracking-wider text-gold hover:text-gold-light">
            ← {t("course.back")}
          </Link>
          <span className="mb-3 block w-fit rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-navy">{course.category}</span>
          <h1 className="max-w-3xl font-display text-3xl font-semibold text-white sm:text-4xl lg:text-5xl" data-testid="course-title">{pick(course, "title")}</h1>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_420px] lg:px-12">
          <div className="space-y-10">
            <Reveal>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {meta.map(([Icon, label, value], i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
                    <Icon className="h-4 w-4 text-gold-hover" />
                    <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
                    <p className="mt-1 text-sm font-semibold text-navy">{value}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <p className="whitespace-pre-line text-base leading-relaxed text-slate-700">{pick(course, "description")}</p>
            </Reveal>
            <Reveal>{listBlock(t("course.objectives"), course[pickKey("objectives")] || course.objectives_en)}</Reveal>
            <Reveal>{listBlock(t("course.outline"), course[pickKey("outline")] || course.outline_en)}</Reveal>
            <Reveal>{listBlock(t("course.outcomes"), course[pickKey("outcomes")] || course.outcomes_en)}</Reveal>
            <Reveal>
              <div className="grid gap-6 rounded-lg bg-slate-50 p-6 sm:grid-cols-2">
                {pick(course, "audience") && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("course.audience")}</h4>
                    <p className="mt-2 text-sm text-slate-700">{pick(course, "audience")}</p>
                  </div>
                )}
                {pick(course, "prerequisites") && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("course.prerequisites")}</h4>
                    <p className="mt-2 text-sm text-slate-700">{pick(course, "prerequisites")}</p>
                  </div>
                )}
                {course.trainer && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("course.trainer")}</h4>
                    <p className="mt-2 text-sm text-slate-700">{course.trainer}</p>
                  </div>
                )}
                {course.registration_deadline && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("course.deadline")}</h4>
                    <p className="mt-2 text-sm text-slate-700">{course.registration_deadline}</p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal className="sticky top-28 rounded-lg border border-slate-200 bg-white p-7 shadow-lg">
              {course.course_state && course.course_state !== "open" ? (
                <div data-testid="course-closed-panel">
                  <span className="inline-block rounded-full bg-slate-100 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t(course.course_state === "completed" ? "course.completed" : "course.closed")}
                  </span>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    {t(course.course_state === "completed" ? "course.completedNote" : "course.closedNote")}
                  </p>
                  <Link
                    to="/training?request=1"
                    data-testid="course-request-upcoming-cta"
                    className="mt-6 inline-block rounded-full bg-gold px-7 py-3 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
                  >
                    {t("course.requestUpcoming")}
                  </Link>
                </div>
              ) : payment ? (
                <div data-testid="payment-instructions-panel">
                  <CheckCircle2 className="h-10 w-10 text-gold" />
                  <h3 className="mt-3 font-display text-2xl font-semibold text-navy">{t("reg.paymentTitle")} — Wise</h3>
                  <div className="mt-4 space-y-2 rounded-lg bg-slate-50 p-4 text-sm">
                    <p><span className="font-semibold text-slate-500">{t("reg.regId")}:</span> <span className="font-mono font-bold text-navy">{payment.registration_id}</span></p>
                    {payment.amount_due != null && (
                      <p><span className="font-semibold text-slate-500">{t("reg.amountDue")}:</span> <span className="font-bold text-navy">{payment.currency} {Number(payment.amount_due).toLocaleString()}</span></p>
                    )}
                  </div>
                  <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-700">{payment[pickKeyLang()] || payment.en}</p>
                  {payDone ? (
                    <p className="mt-5 rounded-lg bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid="payment-submitted-note">{t("reg.payDone")}</p>
                  ) : (
                    <form onSubmit={submitPayment} className="mt-5 space-y-4 border-t border-slate-100 pt-5" data-testid="payment-proof-form">
                      <Field label={t("reg.reference")}>
                        <input className={inputCls} value={refForm.reference} onChange={(e) => setRefForm({ ...refForm, reference: e.target.value })} data-testid="payment-reference-input" />
                      </Field>
                      <Field label={t("reg.proof")}>
                        <input type="file" accept=".pdf,image/*" className="block w-full text-sm text-slate-600 file:me-3 file:rounded-full file:border-0 file:bg-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-white"
                          onChange={(e) => setRefForm({ ...refForm, proof: e.target.files[0] || null })} data-testid="payment-proof-input" />
                      </Field>
                      <button type="submit" disabled={paySending} data-testid="payment-submit-button"
                        className="w-full rounded-full bg-navy py-3 text-sm font-bold text-white transition-colors hover:bg-navy-800 disabled:opacity-60">
                        {paySending ? t("form.sending") : t("reg.paySubmit")}
                      </button>
                    </form>
                  )}
                  <p className="mt-4 text-sm font-semibold text-navy">info@stratedgeuk.com</p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-semibold text-navy">{t("reg.title")}</h3>
                  {(course.fee != null || course.discount_price != null) && (
                    <div className="mt-4 rounded-lg bg-gold-soft p-4" data-testid="course-price-panel">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gold-hover">{t("course.fee")}</p>
                      <div className="mt-1"><PriceTag currency={course.currency} fee={course.fee} discount={course.discount_price} large /></div>
                    </div>
                  )}
                  <form onSubmit={submit} className="mt-5 space-y-4" data-testid="course-registration-form">
                    <Field label={t("form.fullName")} required><input required className={inputCls} value={form.full_name || ""} onChange={set("full_name")} data-testid="reg-name" /></Field>
                    <Field label={t("form.email")} required><input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="reg-email" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label={t("form.phone")}><input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="reg-phone" /></Field>
                      <Field label={t("form.whatsapp")}><input className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="reg-whatsapp" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label={t("form.country")}><input className={inputCls} value={form.country || ""} onChange={set("country")} data-testid="reg-country" /></Field>
                      <Field label={t("form.city")}><input className={inputCls} value={form.city || ""} onChange={set("city")} data-testid="reg-city" /></Field>
                    </div>
                    <Field label={t("form.organization")}><input className={inputCls} value={form.organization || ""} onChange={set("organization")} data-testid="reg-org" /></Field>
                    <Field label={t("form.jobTitle")}><input className={inputCls} value={form.job_title || ""} onChange={set("job_title")} data-testid="reg-jobtitle" /></Field>
                    <Field label={t("reg.preferredDate")}><input type="date" className={inputCls} value={form.preferred_date || ""} onChange={set("preferred_date")} data-testid="reg-date" /></Field>
                    <Field label={t("reg.delivery")}>
                      <select className={inputCls} value={form.delivery_method || ""} onChange={set("delivery_method")} data-testid="reg-delivery">
                        <option value="">—</option>
                        {DELIVERY.map((d) => <option key={d} value={d}>{t(`delivery.${d}`)}</option>)}
                      </select>
                    </Field>
                    <Field label={t("reg.participants")}><input type="number" min="1" className={inputCls} value={form.participants || "1"} onChange={set("participants")} data-testid="reg-participants" /></Field>
                    <Field label={t("reg.notes")}><textarea rows={2} className={inputCls} value={form.notes || ""} onChange={set("notes")} data-testid="reg-notes" /></Field>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="course-register-submit-button"
                      className="w-full rounded-full bg-gold py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover disabled:opacity-60"
                    >
                      {sending ? t("form.sending") : t("training.register")}
                    </button>
                  </form>
                </>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );

  function pickKey(base) {
    return `${base}_${localStorage.getItem("stratedge-lang") || "en"}`;
  }
  function pickKeyLang() {
    return localStorage.getItem("stratedge-lang") || "en";
  }
}
